import React from 'react';
import { useTranslation } from 'react-i18next';
import { AuditLogEntry } from '../../store/actions/AuditLogActions';
import Badge from '../shared/TailwindComponents/Badge';
import { InfoRow, Well } from '../shared/brand/Field';
import { formatDateTime } from '../../utils/functions';
import {
  PiUserLight,
  PiClockLight,
  PiArrowRightLight,
} from 'react-icons/pi';

interface AuditLogDetailModalProps {
  entry: AuditLogEntry;
  onClose: () => void;
}

const getEntityBadgeColor = (entityType: string): 'gray' | 'blue' | 'yellow' | 'green' | 'red' => {
  const colorMap: Record<string, 'gray' | 'blue' | 'yellow' | 'green' | 'red'> = {
    client: 'blue',
    Client: 'blue',
    ClientBilling: 'blue',
    ClientDocument: 'blue',
    ClientLawyer: 'blue',
    matter: 'green',
    Matter: 'green',
    MatterClient: 'green',
    MatterCollaborator: 'green',
    MatterDocument: 'green',
    MatterDraft: 'green',
    MatterInsight: 'green',
    MatterNote: 'green',
    MatterQuestion: 'green',
    MatterSource: 'green',
    MatterTask: 'green',
    file: 'gray',
    File: 'gray',
    folder: 'gray',
    Folder: 'gray',
    task: 'yellow',
    Task: 'yellow',
    review: 'blue',
    review_session: 'blue',
    playbook: 'green',
    research: 'blue',
    table: 'green',
    table_document: 'green',
    template: 'gray',
    Device: 'gray',
    User: 'yellow',
    Organization: 'yellow',
    Team: 'blue',
    TeamMember: 'blue',
    Department: 'blue',
    DepartmentMember: 'blue',
    Role: 'yellow',
    Permission: 'yellow',
    RecycleBin: 'red',
    Tag: 'gray',
    datasource: 'gray',
    CalendarEvent: 'yellow',
  };
  return colorMap[entityType] || 'gray';
};

const getActionBadgeColor = (action: string): 'gray' | 'blue' | 'yellow' | 'green' | 'red' => {
  if (action.includes('creat') || action.includes('upload') || action.includes('restor') || action.includes('add')) return 'green';
  if (action.includes('delet') || action.includes('remov') || action.includes('permanent') || action.includes('logout')) return 'red';
  if (action.includes('updat') || action.includes('chang') || action.includes('renam') || action.includes('process') || action === 'update' || action === 'save' || action.includes('preference') || action.includes('info')) return 'blue';
  if (action.includes('archiv') || action.includes('reset')) return 'yellow';
  return 'gray';
};

/** Group heading inside the modal - sentence case, one size (no eyebrows). */
const GROUP_TITLE = 'mb-2 text-sm font-medium text-fg-strong';

const formatLabel = (str: string) => str.replace(/([a-z])([A-Z])/g, '$1 $2').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

/** Return true for keys that look like internal identifiers (e.g. "tag_id", "Attachment ID", "uuid"). */
const isIdKey = (key: string): boolean => {
  const k = key.toLowerCase().replace(/[\s_-]+/g, '_');
  return (
    k === 'id' ||
    k === 'uuid' ||
    k.endsWith('_id') ||
    k.endsWith('_uuid') ||
    k.endsWith('_encrypted') ||
    k === 'ip_address' ||
    k === 'email' ||
    k === 'user_email'
  );
};

const AuditLogDetailModal: React.FC<AuditLogDetailModalProps> = ({ entry, onClose }) => {
  const { t } = useTranslation();

  const timestamp = entry.created_at ? formatDateTime(entry.created_at) : '—';

  /** Clean up raw backend details strings for display. */
  const formatDetails = (raw: string): string => {
    const logoutMatch = raw.match(/^Logged out all sessions \(count=(\d+)\)$/);
    if (logoutMatch) {
      const count = parseInt(logoutMatch[1], 10);
      if (count === 0) return 'Logged out all sessions (no active sessions)';
      if (count === 1) return 'Logged out 1 session across all devices';
      return `Logged out ${count} sessions across all devices`;
    }
    const permDelMatch = raw.match(/^Permanently deleted: (\d+) folder\(s\), (\d+) file\(s\)$/);
    if (permDelMatch) {
      const folders = parseInt(permDelMatch[1], 10);
      const files = parseInt(permDelMatch[2], 10);
      const parts: string[] = [];
      if (folders) parts.push(`${folders} folder${folders !== 1 ? 's' : ''}`);
      if (files) parts.push(`${files} file${files !== 1 ? 's' : ''}`);
      return parts.length ? `Permanently deleted ${parts.join(' and ')}` : t('Permanently deleted items');
    }
    const sessionMatch = raw.match(/^Logged out session (.{8})$/);
    if (sessionMatch) return 'Logged out a session';
    return raw;
  };

  /** Format a JSON value for human-readable display, handling nested objects/arrays. */
  const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) return '—';
    if (Array.isArray(value)) {
      return value.map(v =>
        typeof v === 'object' && v !== null
          ? Object.entries(v as Record<string, unknown>).filter(([k]) => !isIdKey(k)).map(([k, val]) => `${formatLabel(k)}: ${val}`).join(', ') || JSON.stringify(v)
          : String(v)
      ).join('; ');
    }
    if (typeof value === 'object') {
      return Object.entries(value as Record<string, unknown>).filter(([k]) => !isIdKey(k)).map(([k, val]) => `${formatLabel(k)}: ${val}`).join(', ');
    }
    return String(value);
  };

  const hasChanges = entry.before_json && entry.after_json;
  const beforeJson = entry.before_json || {};
  const afterJson = entry.after_json || {};
  const detailsJson = entry.details_json || {};

  // Collect all changed field keys, excluding ID-like fields
  const changedFields = hasChanges
    ? Array.from(new Set([...Object.keys(beforeJson), ...Object.keys(afterJson)])).filter(k => !isIdKey(k))
    : [];

  const hasDetails = Object.keys(detailsJson).filter((k) => !isIdKey(k)).length > 0;
  const createdWith = !entry.before_json && entry.after_json ? Object.entries(afterJson).filter(([k]) => !isIdKey(k)) : [];
  const deletedState = entry.before_json && !entry.after_json ? Object.entries(beforeJson).filter(([k]) => !isIdKey(k)) : [];

  /*
    Feedback round 3: the old body had its own grey palette, uppercase
    eyebrows and a hand-built table. It now reads like the rest of the app:
    sentence-case group headings, value rows in Wells (which sit grey on the
    glass panel), and the before → after diff as rows rather than a table.
  */
  return (
    <div className="w-[660px] max-w-[90vw] max-h-[70vh] space-y-6 overflow-y-auto px-6 text-start">
      {/* Who, when, what */}
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Badge color={getActionBadgeColor(entry.action)}>{formatLabel(entry.action)}</Badge>
          <Badge color={getEntityBadgeColor(entry.entity_type)}>{formatLabel(entry.entity_type)}</Badge>
        </div>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <div className="flex items-center gap-2 text-sm text-fg-body">
            <PiUserLight className="h-4 w-4 flex-shrink-0 text-fg-muted" />
            <span className="font-medium text-fg-strong">{entry.user_name || t('System')}</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-fg-body">
            <PiClockLight className="h-4 w-4 flex-shrink-0 text-fg-muted" />
            <span>{timestamp}</span>
          </div>
          {entry.entity_created_by_name && (
            <div className="flex items-center gap-2 text-sm text-fg-body sm:col-span-2">
              <span className="text-fg-muted">{t('Created by')}</span>
              <span className="font-medium text-fg-strong">{entry.entity_created_by_name}</span>
              {entry.entity_created_by_role && (
                <Badge color={entry.entity_created_by_role === 'Admin' ? 'yellow' : 'gray'}>
                  {entry.entity_created_by_role}
                </Badge>
              )}
            </div>
          )}
        </div>
      </div>

      {entry.details && (
        <section>
          <h4 className={GROUP_TITLE}>{t('Summary')}</h4>
          <p className="text-sm text-fg-body">{formatDetails(entry.details)}</p>
        </section>
      )}

      {hasDetails && (
        <section>
          <h4 className={GROUP_TITLE}>{t('Details')}</h4>
          <Well className="space-y-2">
            {Object.entries(detailsJson)
              .filter(([key]) => !isIdKey(key))
              .map(([key, value]) => (
                <InfoRow key={key} label={formatLabel(key)}>
                  <span className="min-w-0 break-words text-end text-fg-body">{formatValue(value)}</span>
                </InfoRow>
              ))}
          </Well>
        </section>
      )}

      {hasChanges && changedFields.length > 0 && (
        <section>
          <h4 className={GROUP_TITLE}>{t('Changes')}</h4>
          <Well className="divide-y divide-hairline-soft p-0">
            {changedFields.map((field) => {
              const before = beforeJson[field];
              const after = afterJson[field];
              const changed = String(before ?? '') !== String(after ?? '');
              return (
                <div key={field} className="grid grid-cols-[10rem_1fr] gap-3 px-3 py-2.5 text-sm">
                  <span className="text-fg-muted">{formatLabel(field)}</span>
                  <span className="flex min-w-0 flex-wrap items-center gap-1.5 break-words">
                    <span className={changed ? 'text-danger-600 line-through decoration-danger-300 dark:text-danger-400' : 'text-fg-body'}>
                      {before !== undefined && before !== null ? String(before) : '—'}
                    </span>
                    {changed && (
                      <>
                        <PiArrowRightLight className="h-3 w-3 flex-shrink-0 text-fg-muted rtl:-scale-x-100" />
                        <span className="text-success-700 dark:text-success-400">
                          {after !== undefined && after !== null ? String(after) : '—'}
                        </span>
                      </>
                    )}
                  </span>
                </div>
              );
            })}
          </Well>
        </section>
      )}

      {createdWith.length > 0 && (
        <section>
          <h4 className={GROUP_TITLE}>{t('Created with')}</h4>
          <Well className="space-y-2">
            {createdWith.map(([key, value]) => (
              <InfoRow key={key} label={formatLabel(key)}>
                <span className="min-w-0 break-words text-end text-success-700 dark:text-success-400">{formatValue(value)}</span>
              </InfoRow>
            ))}
          </Well>
        </section>
      )}

      {deletedState.length > 0 && (
        <section>
          <h4 className={GROUP_TITLE}>{t('Deleted state')}</h4>
          <Well className="space-y-2">
            {deletedState.map(([key, value]) => (
              <InfoRow key={key} label={formatLabel(key)}>
                <span className="min-w-0 break-words text-end text-danger-600 dark:text-danger-400">{formatValue(value)}</span>
              </InfoRow>
            ))}
          </Well>
        </section>
      )}

      {!entry.details && Object.keys(detailsJson).length === 0 && !entry.before_json && !entry.after_json && (
        <p className="py-6 text-center text-sm text-fg-muted">{t('No additional details recorded for this entry.')}</p>
      )}
    </div>
  );
};

export default AuditLogDetailModal;
